/**
 * Mic button for the agent chat composer — a port of the CRHQ session UI's
 * VoiceButton (control-room new/src/components/input/VoiceButton.tsx): same
 * icons, same states (idle → recording with a pulsing red button and a
 * running timer → transcribing spinner), same microphone picker when the
 * computer has more than one. Colours follow the Cockpit's light theme.
 */
import { useEffect, useRef, useState } from 'react';
import type { VoiceState } from '@/stores/agentVoiceStore';
import './VoiceButton.css';

interface VoiceButtonProps {
	state: VoiceState;
	duration: number;
	onClick: () => void;
	audioDevices: MediaDeviceInfo[];
	selectedDeviceId: string | null;
	onSelectDevice: (deviceId: string) => void;
	disabled?: boolean;
}

export function formatDuration(seconds: number): string {
	const mins = Math.floor(seconds / 60);
	const secs = seconds % 60;
	return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function VoiceButton({ state, duration, onClick, audioDevices, selectedDeviceId, onSelectDevice, disabled }: VoiceButtonProps) {
	const [showDeviceMenu, setShowDeviceMenu] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);

	// Close menu on outside click
	useEffect(() => {
		if (!showDeviceMenu) return;
		const handleClick = (e: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowDeviceMenu(false);
		};
		document.addEventListener('mousedown', handleClick);
		return () => document.removeEventListener('mousedown', handleClick);
	}, [showDeviceMenu]);

	const showSelector = audioDevices.length > 1;
	const recording = state === 'recording';
	const transcribing = state === 'transcribing';

	let title = 'Voice input';
	if (recording) title = 'Click to stop recording';
	else if (transcribing) title = 'Transcribing...';

	const base = 'flex h-9 w-9 items-center justify-center border transition-all';
	const look = recording
		? 'agent-voice-recording border-red-500 bg-red-500 text-white'
		: transcribing
			? 'cursor-not-allowed border-gray-300 bg-gray-50 text-gray-400 opacity-70'
			: 'border-gray-300 bg-white text-gray-500 hover:border-gray-400 hover:bg-gray-50 hover:text-gray-700';
	const radius = showSelector ? 'rounded-l-lg' : 'rounded-lg';

	return (
		<div className="relative mb-0.5 flex items-center gap-2" ref={menuRef}>
			<div className="flex items-center">
				<button
					type="button"
					className={`${base} ${look} ${radius}`}
					onClick={onClick}
					disabled={transcribing || (disabled && !recording)}
					title={title}
					aria-label={title}
					aria-pressed={recording}
				>
					{transcribing ? <SpinnerIcon /> : <MicIcon />}
				</button>
				{showSelector && (
					<button
						type="button"
						className="flex h-9 w-4 items-center justify-center rounded-r-lg border border-l-0 border-gray-300 bg-white p-0 text-gray-400 transition-all hover:border-gray-400 hover:bg-gray-50 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
						onClick={() => setShowDeviceMenu((v) => !v)}
						title="Select microphone"
						aria-label="Select microphone"
						disabled={recording || transcribing}
					>
						<ChevronIcon />
					</button>
				)}
			</div>
			{recording && (
				<div className="flex items-center gap-1.5" aria-live="polite">
					<span className="agent-voice-dot h-2 w-2 rounded-full bg-red-500" />
					<span className="font-mono text-sm text-red-600">{formatDuration(duration)}</span>
				</div>
			)}
			{showDeviceMenu && showSelector && (
				<div className="absolute bottom-[calc(100%+6px)] right-0 z-50 min-w-[220px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg">
					{audioDevices.map((device) => {
						const active = device.deviceId === (selectedDeviceId || audioDevices[0]?.deviceId);
						return (
							<button
								type="button"
								key={device.deviceId}
								className={`block w-full truncate rounded-md px-3 py-2 text-left text-[13px] hover:bg-gray-50 ${active ? 'text-primary-600' : 'text-gray-600 hover:text-gray-900'}`}
								onClick={() => {
									onSelectDevice(device.deviceId);
									setShowDeviceMenu(false);
								}}
							>
								{device.label || `Microphone (${device.deviceId.slice(0, 8)}...)`}
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
}

function MicIcon() {
	return (
		<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
			<path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
			<path d="M19 10v2a7 7 0 0 1-14 0v-2" />
			<line x1="12" y1="19" x2="12" y2="23" />
			<line x1="8" y1="23" x2="16" y2="23" />
		</svg>
	);
}

function SpinnerIcon() {
	return (
		<svg className="agent-voice-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
			<path d="M12 2a10 10 0 0 1 10 10" />
		</svg>
	);
}

function ChevronIcon() {
	return (
		<svg width="10" height="10" viewBox="0 0 10 6" fill="currentColor" aria-hidden="true">
			<path d="M5 0L0 5h10L5 0z" />
		</svg>
	);
}
